import { supabase } from '@/lib/supabase';
import type { Project, ProductDeploy } from '@/features/projects/types';
import { updateProject } from './update';

export function sanitizeSubdomain(productName: string): string {
  return productName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 63);
}

export async function isSubdomainAvailable(subdomain: string, excludeProjectId?: string): Promise<boolean> {
  let query = supabase
    .from('product_deploy')
    .select('id')
    .eq('subdomain', subdomain);

  if (excludeProjectId) {
    query = query.neq('project_id', excludeProjectId);
  }

  const { data, error } = await query.single();

  // If no data found, subdomain is available
  return !data && error?.code === 'PGRST116';
}

export async function isCustomDomainAvailable(domain: string, excludeProjectId?: string): Promise<boolean> {
  let query = supabase
    .from('product_deploy')
    .select('id')
    .eq('custom_domain', domain);

  if (excludeProjectId) {
    query = query.neq('project_id', excludeProjectId);
  }

  const { data, error } = await query.single();

  // If no data found, domain is available
  return !data && error?.code === 'PGRST116';
}

export async function getDeploymentConfigByCustomDomain(domain: string): Promise<ProductDeploy | null> {
  const { data, error } = await supabase
    .from('product_deploy')
    .select('*')
    .eq('custom_domain', domain)
    .single();

  if (error && error.code !== 'PGRST116') throw error;
  return data as ProductDeploy | null;
}

export async function getDeploymentConfig(projectId: string): Promise<ProductDeploy | null> {
  const { data, error } = await supabase
    .from('product_deploy')
    .select('*')
    .eq('project_id', projectId)
    .single();

  if (error && error.code !== 'PGRST116') throw error;
  return data as ProductDeploy | null;
}

export async function getDeploymentConfigBySubdomain(subdomain: string): Promise<ProductDeploy | null> {
  const { data, error } = await supabase
    .from('product_deploy')
    .select('*')
    .eq('subdomain', subdomain)
    .single();

  if (error && error.code !== 'PGRST116') throw error;
  return data as ProductDeploy | null;
}

export async function saveDeploymentConfig(projectId: string, config: Partial<ProductDeploy>): Promise<ProductDeploy> {
  const { data, error } = await supabase
    .from('product_deploy')
    .upsert({
      project_id: projectId,
      ...config
    }, { onConflict: 'project_id' })
    .select()
    .single();

  if (error) throw error;
  return data as ProductDeploy;
}

export async function deployProject(projectId: string, subdomain?: string): Promise<Project> {
  const updateData: any = {
    status: 'published'
  };

  if (subdomain) {
    updateData.subdomain = subdomain;
    updateData.published_url = `https://${subdomain}.waitlistly.live`;
  }

  const { data, error } = await supabase
    .from('projects')
    .update(updateData)
    .eq('id', projectId)
    .select()
    .single();

  if (error) throw error;
  return data as Project;
}

export async function publishProject(projectId: string, deployConfig: Partial<ProductDeploy>): Promise<void> {
  const now = new Date().toISOString();

  // Update or insert deployment config with deployed status
  await saveDeploymentConfig(projectId, {
    ...deployConfig,
    deployment_status: 'deployed',
    deployed_at: deployConfig.deployed_at || now,
    last_published_at: now
  });

  // Update project status to published
  await updateProject(projectId, {
    status: 'published',
    subdomain: deployConfig.subdomain,
    published_url: `https://${deployConfig.subdomain}.waitlistly.live`
  });
}

export async function updatePaymentStatus(
  projectId: string,
  status: 'pending' | 'paid' | 'failed',
  details?: { paymentId?: string; amount?: number }
): Promise<ProductDeploy> {
  const updateData: any = {
    payment_status: status
  };

  if (details?.paymentId) updateData.payment_id = details.paymentId;
  if (details?.amount) updateData.amount_paid = details.amount;

  return saveDeploymentConfig(projectId, updateData);
}
